/**
 * ConfigureFamilyPage - Customer-facing configurator for non-Vinyl product families
 * (CD, DVD, Blu-ray).
 *
 * Phase 3 of the Multi-Product Architecture: this page is the customer entry-point
 * for /configure/cd, /configure/dvd, /configure/bluray. It deliberately does NOT
 * touch the Vinyl flow (Vinyl continues to use OrderProductPage at /orders/new/product).
 *
 * Behavior:
 *  - Reads `:family` from the URL.
 *  - Validates that:
 *    - The family is one of cd|dvd|bluray (vinyl → redirect to /orders/new/product).
 *    - The active company has `family` in `enabled_order_types`.
 *  - Looks up whether a Visual or Expert flow exists for the family.
 *    - If yes → renders <ProductConfigWizard productFamily={family} />
 *    - If no  → shows a friendly empty-state with a CTA to /orders/new/simple?type={family}
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ordersApi } from '../lib/api';
import { Header } from '../components/Header';
import { LoadingSpinner } from '../components/LoadingSpinner';
import ProductConfigWizard from '../components/ProductConfigWizard';
import CatalogueNumberInput from '../components/CatalogueNumberInput';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Disc, Film, Tv, ArrowRight, AlertCircle } from 'lucide-react';
import { useTranslation } from '../contexts/TranslationContext';

import { API_BASE } from '../lib/apiBase';
const API_URL = API_BASE;

const FAMILY_META = {
  cd:     { label: 'CD',      icon: Disc, productType: 'cd' },
  dvd:    { label: 'DVD',     icon: Film, productType: 'dvd' },
  bluray: { label: 'Blu-ray', icon: Tv,   productType: 'bluray' },
};

const VALID_FAMILIES = Object.keys(FAMILY_META);

export default function ConfigureFamilyPage() {
  const { family } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [checking, setChecking] = useState(true);
  const [flowAvailable, setFlowAvailable] = useState(null); // null = unknown, true/false after probe
  const [familyEnabled, setFamilyEnabled] = useState(null);
  const [editSpecs, setEditSpecs] = useState({
    quantity: 250,
  });
  const [isSaving, setIsSaving] = useState(false);

  const [orderTitle, setOrderTitle] = useState('');
  const [catalogueNumber, setCatalogueNumber] = useState('');
  const [catalogueValid, setCatalogueValid] = useState(true);
  const [customerPoNumber, setCustomerPoNumber] = useState('');
  const titleInputRef = useRef(null);
  // Mirror OrderProductPage: switch to full-screen immersive layout when the
  // user picks Visual Builder mode. Without this, the shared FlowDrivenBuilder
  // renders inside a cramped max-w-4xl card and looks like a different UI
  // than the Vinyl flow.
  const [configMode, setConfigMode] = useState(null); // null | 'quick' | 'visual' | 'expert'
  const isImmersiveMode = configMode === 'visual';

  // Hard-redirect Vinyl to the existing route — keep Vinyl flow untouched
  useEffect(() => {
    if (family === 'vinyl') {
      navigate('/orders/new/product', { replace: true });
    }
  }, [family, navigate]);

  // Probe: is family enabled for org + does an active flow exist?
  useEffect(() => {
    if (!family || !VALID_FAMILIES.includes(family)) return;

    let cancelled = false;
    const probe = async () => {
      setChecking(true);
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // 1. Get active company id, then fetch enabled order types for it
        const meRes = await fetch(`${API_URL}/api/auth/me`, { headers });
        let companyId = null;
        if (meRes.ok) {
          const me = await meRes.json();
          companyId = me?.active_company_id || me?.organization_id;
        }
        if (companyId) {
          try {
            const typesRes = await fetch(
              `${API_URL}/api/organizations/${companyId}/order-types`,
              { headers }
            );
            if (typesRes.ok) {
              const data = await typesRes.json();
              const enabled = data?.enabled_order_types || ['vinyl'];
              if (!cancelled) setFamilyEnabled(enabled.includes(family));
            } else if (!cancelled) {
              setFamilyEnabled(true); // best-effort; backend will 403 on create
            }
          } catch {
            if (!cancelled) setFamilyEnabled(true);
          }
        } else if (!cancelled) {
          setFamilyEnabled(true);
        }

        // 2. Probe whether an active Visual flow exists for this family
        const flowRes = await fetch(
          `${API_URL}/api/product-config/flows/by-mode/visual?product_family=${encodeURIComponent(family)}`,
          { headers }
        );
        if (cancelled) return;
        if (flowRes.ok) {
          const flow = await flowRes.json();
          // Endpoint returns null when no active flow exists (response_model=Optional[FlowResponse])
          setFlowAvailable(!!(flow && flow.id));
        } else {
          setFlowAvailable(false);
        }
      } catch (err) {
        console.error('[ConfigureFamilyPage] Probe failed:', err);
        if (!cancelled) setFlowAvailable(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    };

    probe();
    return () => { cancelled = true; };
  }, [family]);

  // Submit handler — create the order with product_type = family
  const handleConfigComplete = useCallback(async (newSpecs) => {
    if (!orderTitle.trim()) {
      toast.error(t('configure.title_required', 'Title is required'));
      titleInputRef.current?.focus();
      return;
    }
    if (!catalogueValid) {
      toast.error(t('configure.fix_catalogue', 'Please fix the catalogue number before continuing.'));
      return;
    }
    setIsSaving(true);
    try {
      const cleanSpecs = { ...newSpecs };
      const flowId = cleanSpecs._flow_id;
      const flowVersion = cleanSpecs._flow_version;
      const flowSelections = cleanSpecs._flow_selections;
      delete cleanSpecs._flow_id;
      delete cleanSpecs._flow_version;
      delete cleanSpecs._flow_selections;

      const orderData = {
        order_type: 'new_production',
        product_type: FAMILY_META[family].productType,
        title: orderTitle.trim(),
        vinyl_specs: cleanSpecs, // shared specs blob, repurposed (backend already accepts for non-vinyl)
      };
      if (catalogueNumber.trim()) orderData.catalogue_number = catalogueNumber.trim();
      if (customerPoNumber.trim()) orderData.customer_po_number = customerPoNumber.trim();
      if (flowId) orderData.config_flow_id = flowId;
      if (flowVersion) orderData.config_flow_version = flowVersion;
      if (flowSelections) orderData.config_selections = flowSelections;

      const response = await ordersApi.create(orderData);
      toast.success(t('configure.order_created', '{label} order created', { label: FAMILY_META[family].label }));
      navigate(`/orders/${response.data.id}/files`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('simple_order.create_failed', 'Failed to create order'));
    } finally {
      setIsSaving(false);
    }
  }, [family, orderTitle, catalogueNumber, customerPoNumber, catalogueValid, navigate, t]);

  // ============== INVALID FAMILY ==============
  if (!family || (!VALID_FAMILIES.includes(family) && family !== 'vinyl')) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-12">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <h2 className="font-semibold text-red-900">{t('configure.unknown_family_title', 'Unknown product family')}</h2>
              <p className="text-sm text-red-700 mt-1">
                {t('configure.unknown_family_desc', '"{family}" is not a recognised product family. Please return to your dashboard and pick a product to order.', { family: String(family || '') })}
              </p>
              <Button onClick={() => navigate('/dashboard')} className="mt-3">
                {t('common.back_to_dashboard_lower', 'Back to dashboard')}
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (family === 'vinyl') {
    return null; // Redirect useEffect handles it
  }

  // ============== LOADING ==============
  if (checking) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-12 flex justify-center">
          <LoadingSpinner />
        </main>
      </div>
    );
  }

  const meta = FAMILY_META[family];
  const Icon = meta.icon;

  // ============== FAMILY NOT ENABLED ==============
  if (familyEnabled === false) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-12">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 flex items-start gap-3" data-testid={`family-not-enabled-${family}`}>
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h2 className="font-semibold text-amber-900 flex items-center gap-2">
                <Icon className="w-5 h-5" /> {t('configure.family_not_enabled_title', '{label} ordering is not enabled', { label: meta.label })}
              </h2>
              <p className="text-sm text-amber-800 mt-1">
                {t('configure.family_not_enabled_desc', 'Your company is not currently enabled for {label} orders. Please contact your administrator.', { label: meta.label })}
              </p>
              <Button onClick={() => navigate('/dashboard')} className="mt-3">
                {t('common.back_to_dashboard_lower', 'Back to dashboard')}
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ============== NO FLOW CONFIGURED YET (graceful fallback) ==============
  if (flowAvailable === false) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-12">
          <div
            className="bg-white rounded-xl border border-slate-200 p-8 text-center"
            data-testid={`no-flow-${family}`}
          >
            <div className="inline-flex items-center justify-center w-14 h-14 bg-slate-100 rounded-full mb-4">
              <Icon className="w-7 h-7 text-slate-500" />
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              {t('configure.no_flow_title', "We're working on a visual configurator for {label}", { label: meta.label })}
            </h2>
            <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
              {t('configure.no_flow_desc', "The full visual {label} configurator isn't quite ready yet. In the meantime, you can place a {label} order using our simple order form — same delivery, same support, just a more compact setup.", { label: meta.label })}
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button
                onClick={() => navigate(`/orders/new/simple?type=${meta.productType}`)}
                size="lg"
                data-testid={`fallback-simple-form-${family}`}
              >
                {t('configure.use_simple_form', 'Use simple {label} order form', { label: meta.label })}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                {t('common.back_to_dashboard_lower', 'Back to dashboard')}
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ============== IMMERSIVE VISUAL BUILDER (matches Vinyl) ==============
  // Full-screen layout so the FlowDrivenBuilder gets the same premium
  // presentation as the Vinyl flow — big option cards, wide summary
  // sidebar, no cramped max-w-4xl wrapper.
  if (isImmersiveMode) {
    return (
      <div
        className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100"
        data-testid={`configure-family-${family}-immersive`}
      >
        <main className="pb-8">
          <ProductConfigWizard
            mode={configMode}
            productFamily={family}
            initialSpecs={editSpecs}
            onConfigChange={(newSpecs) => setEditSpecs(newSpecs)}
            onComplete={handleConfigComplete}
            onModeChange={(nextMode) => setConfigMode(nextMode)}
            pricingMessage={t('configure.pricing_message', 'See your full {label} cost instantly', { label: meta.label })}
            isImmersiveMode={true}
            orderMetadata={{
              title: orderTitle,
              catalogueNumber,
              onTitleChange: setOrderTitle,
              onCatalogueChange: setCatalogueNumber,
            }}
          />
        </main>
      </div>
    );
  }

  // ============== FULL CONFIGURATOR (Visual / Expert / Quick) ==============
  return (
    <div className="min-h-screen bg-background" data-testid={`configure-family-${family}`}>
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Family label */}
        <div className="mb-6 flex items-center gap-2 text-sm">
          <Icon className="w-5 h-5 text-indigo-600" />
          <span className="font-medium text-slate-900">{t('configure.configuring_order', 'Configuring {label} order', { label: meta.label })}</span>
        </div>

        {/* Order details — same as OrderProductPage */}
        <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-end">
          <div className="flex-1 w-full sm:w-auto">
            <label className="text-xs font-medium text-slate-500 mb-1 block">
              {t('order.title_label', 'Title')} <span className="text-red-400">*</span>
            </label>
            <Input
              ref={titleInputRef}
              placeholder={t('order.title_artist_placeholder', 'Artist Name - Album Title')}
              value={orderTitle}
              onChange={(e) => setOrderTitle(e.target.value)}
              className="h-9"
              data-testid="order-title-input"
            />
          </div>
          <div className="w-full sm:w-48">
            <CatalogueNumberInput
              label={t('order.catalogue_short', 'Catalogue #')}
              value={catalogueNumber}
              onChange={setCatalogueNumber}
              onValidityChange={setCatalogueValid}
              placeholder="CAT-001"
              data-testid="catalogue-number-input"
            />
          </div>
          <div className="w-full sm:w-40">
            <label className="text-xs font-medium text-slate-500 mb-1 block">
              {t('order.po_number_label', 'PO Number')}
            </label>
            <Input
              placeholder="PO-12345"
              value={customerPoNumber}
              onChange={(e) => setCustomerPoNumber(e.target.value)}
              className="h-9"
              data-testid="customer-po-number-input"
            />
          </div>
        </div>

        {/* Shared configurator engine */}
        <div className="bg-white rounded-xl border border-border p-6">
          <ProductConfigWizard
            mode={configMode}
            productFamily={family}
            initialSpecs={editSpecs}
            onConfigChange={(newSpecs) => setEditSpecs(newSpecs)}
            onComplete={handleConfigComplete}
            onModeChange={(nextMode) => setConfigMode(nextMode)}
            pricingMessage={t('configure.pricing_message', 'See your full {label} cost instantly', { label: meta.label })}
            isImmersiveMode={false}
            orderTitle={orderTitle}
            titleInputRef={titleInputRef}
          />
        </div>

        {isSaving && (
          <p className="text-xs text-slate-500 mt-3 text-center">{t('configure.saving_order', 'Saving order…')}</p>
        )}
      </main>
    </div>
  );
}
